/**
 * Staff user page, "What this user can do": turns `GET /admin/iam/users/:id/effective-permissions`
 * into areas (Applications, Vendors, IAM…) of view / change actions with a held flag and a plain
 * source ("Super Admin", "Loan Reviewers", "role: IAM Admin"), plus the one-line summary. Pure, so
 * the panel and its tests share it.
 */

export type PermissionRisk = "standard" | "sensitive" | "critical";

export type EffectivePermissionInput = {
  key: string;
  label: string;
  group: string;
  risk: PermissionRisk;
  requiresMfa: boolean;
  grantedBy?: Array<{ roleId: string; roleKey: string; roleName: string }>;
  grantedByGroups?: Array<{ groupId: string; groupName: string }>;
};

export type EffectivePermissionsInput = {
  model: "v1" | "v2";
  decided: string[];
  superAdmin?: boolean;
  groups?: Array<{ id: string; name: string }>;
  permissions: EffectivePermissionInput[];
  notHeld?: EffectivePermissionInput[];
};

/** `view` = read-only access; `change` = edits, decisions and other actions. */
export type PermissionKind = "view" | "change";

export type PermissionRow = {
  key: string;
  label: string;
  kind: PermissionKind;
  risk: PermissionRisk;
  requiresMfa: boolean;
  /** Granted by the model that decides access today. */
  held: boolean;
  /** Granted by a role but not by the deciding model (v1 shadow): shown as "after switch to roles". */
  roleOnly: boolean;
  /** Where it comes from, plain words ("Super Admin", group and role names); empty when not held. */
  sources: string[];
};

export type PermissionArea = {
  name: string;
  rows: PermissionRow[];
  heldCount: number;
};

export function permissionKind(key: string): PermissionKind {
  return /\.read$/.test(key) ? "view" : "change";
}

/**
 * What grants a held key under the deciding model: under v1 the Super Admin role, else the groups
 * (falling back to roles if none is reported); under v2 the roles.
 */
function permissionSources(
  p: EffectivePermissionInput,
  data: EffectivePermissionsInput,
): string[] {
  const roleNames = (p.grantedBy ?? []).map((g) => `role: ${g.roleName}`);
  if (data.model === "v2") return roleNames;
  if (data.superAdmin) return ["Super Admin"];
  const groupNames = (p.grantedByGroups ?? []).map((g) => g.groupName);
  return groupNames.length > 0 ? groupNames : roleNames;
}

/** Areas in catalog order; within an area, view actions first. */
export function buildPermissionAreas(
  data: EffectivePermissionsInput,
): PermissionArea[] {
  const decided = new Set(data.decided);
  const all = [...data.permissions, ...(data.notHeld ?? [])];
  const areas = new Map<string, PermissionRow[]>();
  for (const p of all) {
    const held = decided.has(p.key);
    const rows = areas.get(p.group) ?? [];
    rows.push({
      key: p.key,
      label: p.label,
      kind: permissionKind(p.key),
      risk: p.risk,
      requiresMfa: p.requiresMfa,
      held,
      roleOnly: !held && (p.grantedBy ?? []).length > 0,
      sources: held ? permissionSources(p, data) : [],
    });
    areas.set(p.group, rows);
  }
  return [...areas.entries()].map(([name, rows]) => {
    const sorted = [...rows].sort((a, b) =>
      a.kind === b.kind ? 0 : a.kind === "view" ? -1 : 1,
    );
    return {
      name,
      rows: sorted,
      heldCount: sorted.filter((r) => r.held).length,
    };
  });
}

/** Where this user's access comes from, as one sentence. */
export function accessSourceSummary(data: EffectivePermissionsInput): string {
  if (data.model === "v1") {
    if (data.superAdmin)
      return "Super Admin: has every permission. Groups and roles add nothing.";
    const groups = (data.groups ?? []).map((g) => g.name);
    if (groups.length === 0)
      return "Not in any group, so has no access yet. Add them to a group.";
    return `Access comes from ${groups.length === 1 ? "the group" : "the groups"} ${joinNames(groups)}.`;
  }
  const roles = [
    ...new Set(
      data.permissions.flatMap((p) =>
        (p.grantedBy ?? []).map((g) => g.roleName),
      ),
    ),
  ].sort((a, b) => a.localeCompare(b));
  if (roles.length === 0) return "Has no active role, so has no access.";
  return `Access comes from ${roles.length === 1 ? "the role" : "the roles"} ${joinNames(roles)}.`;
}

export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export type PermissionTotals = {
  held: number;
  total: number;
  critical: number;
  needMfa: number;
};

export function permissionTotals(
  areas: readonly PermissionArea[],
): PermissionTotals {
  const rows = areas.flatMap((a) => a.rows);
  const held = rows.filter((r) => r.held);
  return {
    held: held.length,
    total: rows.length,
    critical: held.filter((r) => r.risk === "critical").length,
    needMfa: held.filter((r) => r.requiresMfa).length,
  };
}

/** Areas filtered by search text (label, key or area name) and the "only what they can do" toggle. */
export function filterAreas(
  areas: readonly PermissionArea[],
  opts: { query?: string; heldOnly?: boolean },
): PermissionArea[] {
  const q = (opts.query ?? "").trim().toLowerCase();
  return areas
    .map((a) => {
      const areaMatch = q !== "" && a.name.toLowerCase().includes(q);
      const rows = a.rows.filter(
        (r) =>
          (!opts.heldOnly || r.held || r.roleOnly) &&
          (q === "" ||
            areaMatch ||
            r.label.toLowerCase().includes(q) ||
            r.key.toLowerCase().includes(q)),
      );
      return { ...a, rows };
    })
    .filter((a) => a.rows.length > 0);
}
